import {useCallback} from 'react'
import {useMappedState} from 'redux-react-hook'
import PERFORMANCE from 'performance'
import useRouter from 'hooks/useRouter'
import useOrderbook from 'hooks/useOrderbook'

import useLeverageData from 'hooks/useLeverageData'
import {
  marginRequiredFn,
  initialMarginNotionalFn,
  totalInitialMarginFn,
  unrealizedPnlFn,
  findCurLeverageByLevel,
  calculateAssumePrice
} from 'utils/futures'
import {formatNumber} from 'utils/format'
import ApiSdk from 'services/callApi'
import {TRADE_TYPES, TRADE_SIDES, CONTRACT_MULTIPLIER} from 'constants'

const setVal = (v, c = '') => (v === null || v === undefined ? c : v)
const {DEPTH_LIMIT} = PERFORMANCE

function useOrderForm (symbol, quoteAsset = 'USDT', formType) {
  const router = useRouter()
  const {lng = 'en'} = router.query
  const FilterTypes = {
    PRICE_FILTER: 'PRICE_FILTER',
    LOT_SIZE: 'LOT_SIZE',
    MARKET_LOT_SIZE: 'MARKET_LOT_SIZE'
  }
  const mapState = state => {
    const isMarket = formType === TRADE_TYPES.MARKET
    const limitType = state.orderForm?.[formType]?.limitType
    const timeInForce = state.orderForm?.[formType]?.timeInForce
    const reduceOnly = state.orderForm?.[formType]?.reduceOnly
    const entities = state?.entities || {}
    const markPrice = entities.tickers?.[symbol]?.markPrice || 0
    const filters = entities.exchangeInfos?.[symbol]?.filters || []
    const maxPrice = filters.find(item => FilterTypes.PRICE_FILTER === item?.filterType)?.maxPrice
    const limitMaxQty = filters.find(item => FilterTypes.LOT_SIZE === item?.filterType)?.maxQty
    const marketMaxQty = filters.find(item => FilterTypes.MARKET_LOT_SIZE === item?.filterType)
      ?.maxQty
    const tickSize = filters.find(item => FilterTypes.PRICE_FILTER === item?.filterType)?.tickSize
    const buyPrice = isMarket ? markPrice : state?.orderForm?.[formType]?.[TRADE_SIDES.BUY]?.price
    const buyQty = state.orderForm?.[formType]?.[TRADE_SIDES.BUY]?.orderQty
    const buyStopPrice = state.orderForm?.[formType]?.[TRADE_SIDES.BUY]?.stopPrice
    const buyStatus = state.orderForm?.[formType]?.[TRADE_SIDES.BUY]?.tradeStatus

    const sellPrice = isMarket ? markPrice : state?.orderForm?.[formType]?.[TRADE_SIDES.SELL]?.price
    const sellQty = state.orderForm?.[formType]?.[TRADE_SIDES.SELL]?.orderQty
    const sellStopPrice = state.orderForm?.[formType]?.[TRADE_SIDES.SELL]?.stopPrice
    const sellStatus = state.orderForm?.[formType]?.[TRADE_SIDES.SELL]?.tradeStatus
    const positions = state?.entities?.positions || {}
    const positionSize = Number(positions?.[symbol]?.positionSize) || 0
    return {
      positions,
      positionSize,
      balance: state?.entities?.balances || {},
      buyPrice: setVal(buyPrice),
      sellPrice: setVal(sellPrice),
      buyQty: setVal(buyQty),
      sellQty: setVal(sellQty),
      buyStopPrice: setVal(buyStopPrice),
      sellStopPrice: setVal(sellStopPrice),
      maxPrice: setVal(maxPrice),
      limitMaxQty: setVal(limitMaxQty),
      marketMaxQty: setVal(marketMaxQty),
      tickSize,
      buyStatus,
      sellStatus,
      limitType,
      timeInForce,
      reduceOnly,
      markPrice
    }
  }

  const {
    positions,
    positionSize,
    tickSize,
    balance,
    maxPrice,
    limitMaxQty,
    marketMaxQty,
    buyPrice,
    sellPrice,
    buyQty,
    sellQty,
    buyStopPrice,
    sellStopPrice,
    buyStatus,
    sellStatus,
    limitType,
    timeInForce,
    reduceOnly,
    markPrice
  } = useMappedState(mapState)

  const {leverageData} = useLeverageData()
  const {curLeverage: leverage} = leverageData
  const {bracket_notional_cap} = findCurLeverageByLevel(leverageData)

  // calculate form data required
  const curPosition = positions[symbol] || {}
  const initialMarginNotional = initialMarginNotionalFn(curPosition)
  const totalInitialMargin = Number(totalInitialMarginFn(initialMarginNotional, leverage))
  const unrealizedPnl = Number(unrealizedPnlFn(curPosition))
  const availableBalance = Math.max(Number(balance?.[quoteAsset]?.walletBalance) + unrealizedPnl - totalInitialMargin || 0, 0)

  const asks = useOrderbook({
    symbol,
    type: 'ask',
    limit: DEPTH_LIMIT,
    tickSize,
    mergeDesc: false,
    orderDesc: true
  })

  const bids = useOrderbook({
    symbol,
    type: 'bid',
    limit: DEPTH_LIMIT,
    tickSize,
    mergeDesc: true,
    orderDesc: true
  })

  const bidPrice = bids[0]?.price
  const askPrice = asks[asks.length - 1]?.price

  const marginBuyRequired = marginRequiredFn({
    entities: positions,
    symbol,
    type: 'bid',
    orderQty: buyQty,
    price: buyPrice,
    bidPrice,
    askPrice,
    formType,
    leverage
  })
  const marginSellRequired = marginRequiredFn({
    entities: positions,
    symbol,
    type: 'ask',
    orderQty: sellQty,
    price: sellPrice,
    bidPrice,
    askPrice,
    formType,
    leverage,
  })

  let marginBuyUnitRequired = marginRequiredFn({
    entities: positions,
    symbol,
    type: 'bid',
    orderQty: 1,
    price: buyPrice,
    totalInitialMargin,
    bidPrice,
    askPrice,
    formType,
    leverage,
  })

  marginBuyUnitRequired = marginBuyUnitRequired ? Number(marginBuyUnitRequired) : 0

  let marginSellUnitRequired = marginRequiredFn({
    entities: positions,
    symbol,
    type: 'ask',
    orderQty: 1,
    price: sellPrice,
    totalInitialMargin,
    bidPrice,
    askPrice,
    formType,
    leverage
  })

  let maxBuyQty = 0
  let maxSellQty = 0
  marginSellUnitRequired = marginSellUnitRequired ? Number(marginSellUnitRequired) : 0

  if (reduceOnly) {
    if (positionSize) {
      positionSize > 0 && (maxSellQty = positionSize)
      positionSize < 0 && (maxBuyQty = Math.abs(positionSize))
    }
  } else {
    const calcBuyPrice = calculateAssumePrice({formType, type: 'bid', askPrice, bidPrice, iptPrice: buyPrice, markPrice})
    const calcSellPrice = calculateAssumePrice({formType, type: 'ask', askPrice, bidPrice, iptPrice: sellPrice, markPrice})
    maxBuyQty = marginBuyUnitRequired ? availableBalance / marginBuyUnitRequired : 0
    maxBuyQty = calcBuyPrice ? Math.min(maxBuyQty, bracket_notional_cap / (calcBuyPrice * CONTRACT_MULTIPLIER)) : maxBuyQty
    maxSellQty = marginSellUnitRequired ? availableBalance / marginSellUnitRequired : 0
    maxSellQty = calcSellPrice ? Math.min(bracket_notional_cap / (calcSellPrice * CONTRACT_MULTIPLIER), maxSellQty) : maxSellQty
    if (positionSize > 0) {
      // maxBuyQty = Math.max(maxBuyQty - positionSize, 0)
      maxSellQty += positionSize
    } else {
      // maxSellQty = Math.max(maxSellQty - Math.abs(positionSize), 0)
      maxBuyQty += Math.abs(positionSize)
    }
  }

  const orderBuyValue = buyQty * buyPrice
  const orderSellValue = sellQty * sellPrice

  // handle operations in form
  const handleBuy = useCallback(() => {
    const isTakeProfit = formType === TRADE_TYPES.TAKE_PROFIT
    let params = {
      symbol,
      quantity: Number(buyQty),
      price: Number(buyPrice),
      type: isTakeProfit ? 'TAKE_PROFIT' : formType,
      timeInForce,
      reduceOnly: !!reduceOnly
    }
    if (formType === TRADE_SIDES.CLOSE) {
      params = {...params, type: TRADE_SIDES.LIMIT}
    } else if (formType === TRADE_TYPES.STOP || isTakeProfit) {
      params = {...params, stopPrice: Number(buyStopPrice)}
    }
    return ApiSdk.futures.placeBuyOrder(params, {lang: lng})
  }, [buyPrice, buyQty, buyStopPrice, formType, lng, reduceOnly, symbol, timeInForce])

  const handleSell = useCallback(() => {
    const isTakeProfit = formType === TRADE_TYPES.TAKE_PROFIT
    let params = {
      symbol,
      quantity: Number(sellQty),
      price: Number(sellPrice),
      type: isTakeProfit ? 'TAKE_PROFIT' : formType,
      timeInForce,
      reduceOnly: !!reduceOnly
    }
    if (formType === TRADE_SIDES.CLOSE) {
      params = {...params, type: TRADE_SIDES.LIMIT}
    } else if (formType === TRADE_TYPES.STOP || isTakeProfit) {
      params = {...params, stopPrice: Number(sellStopPrice)}
    }
    return ApiSdk.futures.placeSellOrder(params, {lang: lng})
  }, [formType, lng, reduceOnly, sellPrice, sellQty, sellStopPrice, symbol, timeInForce])

  return {
    availableBalance: Math.max(availableBalance, 0),
    marginBuyRequiredVal: marginBuyRequired || 0,
    marginBuyRequired: marginBuyRequired
      ? formatNumber(marginBuyRequired)
      : buyQty && buyPrice
        ? 0
        : '',
    marginBuyUnitRequired: marginBuyUnitRequired
      ? formatNumber(marginBuyUnitRequired)
      : buyQty && buyPrice
        ? 0
        : '',
    marginSellRequiredVal: marginSellRequired || 0,
    marginSellRequired: marginSellRequired
      ? formatNumber(marginSellRequired)
      : sellQty && sellPrice
        ? 0
        : '',
    marginSellUnitRequired: marginSellUnitRequired
      ? formatNumber(marginSellUnitRequired)
      : sellQty && sellPrice
        ? 0
        : '',
    orderBuyValue: orderBuyValue ? formatNumber(orderBuyValue) : '',
    orderSellValue: orderSellValue ? formatNumber(orderSellValue) : '',
    handleBuy,
    handleSell,
    maxPrice,
    limitMaxQty,
    marketMaxQty,
    buyPrice,
    sellPrice,
    buyQty,
    sellQty,
    buyStopPrice,
    sellStopPrice,
    buyStatus,
    sellStatus,
    limitType,
    timeInForce,
    reduceOnly,
    positionSize,
    maxBuyQty,
    maxSellQty
  }
}

export default useOrderForm
